import * as React from 'react';
import * as rp from 'request-promise';

import {
  createMuiTheme, createStyles, Grid, MuiThemeProvider, Theme, withStyles, WithStyles
} from '@material-ui/core';
import { blueGrey, pink } from '@material-ui/core/colors';

import {
  AddRoleRequest, AuthUserDetails, DeleteRoleRequest, RoleDetails, ServiceDetails, UserDetails,
  UserWithRoles
} from './models/entities';
import NavBar from './NavBar';
import RoleTable from './RoleTable';
import { getServiceDefaultRoles } from './ServiceRoles';

// import RoleTable from './RoleTable';

const muiTheme = createMuiTheme({
  palette: {
    primary: blueGrey,
    secondary: pink
  },
  typography: {
    useNextVariants: true
  }
});

function getRoleLocation() {
  return location.origin;
}

function getAuthLocation() {
  return getRoleLocation().replace("role.", "auth.");
}

function login() {
  window.location.replace(getAuthLocation() + "/login.html?redirect=" + encodeURIComponent(window.location.href));
}

function authorize() {
  rp({
    uri: getRoleLocation() + "/dev/v1/authorization/authorize",
    method: "POST",
    body: JSON.stringify({
      serviceId: "role"
    }),
    withCredentials: true,
  }).then((data) => {
    console.log(JSON.stringify(data));
    window.location.reload();
  });
}

async function getAllServices(): Promise<{ [key: string]: ServiceDetails }> {
  const roles: { [key: string]: RoleDetails } = {};
  ['role0', 'role1', 'role2'].forEach(roleName => roles[roleName] = { name: roleName, description: `${roleName} granted`, defaultTTL: 300 })
  let serviceRoleMap: { [key: string]: ServiceDetails } = {};
  ['service0', 'service1', 'service2'].forEach(serviceId => serviceRoleMap[serviceId] = {
    roles,
    serviceId
  });

  serviceRoleMap = {
    ...serviceRoleMap,
    ...getServiceDefaultRoles()
  }

  return serviceRoleMap;
}

async function getUserWithRolesForTenantService(getRequest: { serviceId: string }): Promise<UserWithRoles[]> {
  const rolesUrl = getRoleLocation() + "/dev/v1/roles/tenant-service";
  const requestUrl = rolesUrl + '?' + Object.keys(getRequest).map(k => `${encodeURIComponent(k)}=${encodeURIComponent(getRequest[k])}`).join('&')
  try {
    const result = await rp.get(requestUrl);
    return JSON.parse(result) as UserWithRoles[];
  }
  catch (e) {
    console.log(JSON.stringify(e.error));
    if (JSON.stringify(e.error).split('hue2_login_token').length === 3) {
      login();
    }
    if (JSON.stringify(e.error).split('hue2_role_token').length === 3 || JSON.stringify(e.error).includes("ROLE_EXPIRED")) {
      authorize();
    }
    return [];
  }
}

async function getAuthUserMap(): Promise<{ [key: string]: AuthUserDetails }> {
  let allUsers: AuthUserDetails[] = [];
  const result = await rp({
    uri: getAuthLocation() + "/dev/v1/users",
    method: "GET",
    withCredentials: true
  });
  allUsers = (JSON.parse(result)).users;
  const allUserMap: { [key: string]: AuthUserDetails } = {};
  allUsers.forEach(user => allUserMap[user.userId] = user)
  return allUserMap;
}

async function getUserRoleMap(allUserMap: { [key: string]: AuthUserDetails }, serviceRoleMap: { [key: string]: ServiceDetails }): Promise<{ [key: string]: UserDetails }> {
  const userRoleMap: { [key: string]: UserDetails } = {};
  for (const service of Object.keys(serviceRoleMap).map(key => serviceRoleMap[key])) {
    const serviceRoles = await getUserWithRolesForTenantService({ serviceId: service.serviceId });
    serviceRoles
      .filter(u => allUserMap[u.userId])
      .forEach(user => {
        userRoleMap[`${user.userId}#${service.serviceId}`] = {
          email: allUserMap[user.userId].email,
          roles: user.roles,
          serviceId: user.serviceId,
          userId: user.userId,
          username: allUserMap[user.userId].username
        }
      });
  }
  return userRoleMap;
}

const addRole = async (addRoleRequest: AddRoleRequest, rolesUrl: string) => {
  try {
    const result = JSON.parse(await rp.post(rolesUrl, {
      body: JSON.stringify(addRoleRequest)
    }));
    return { success: true, data: result };
  } catch (e) {
    if (JSON.stringify(e.error).includes("ROLE_EXPIRED")) {
      authorize();
    }
    console.log("AddRole Failed: ", e.error);
    return { success: false, error: JSON.parse(e.error) };
  }
}

const deleteRole = async (deleteRoleRequest: DeleteRoleRequest, rolesUrl: string) => {
  try {
    const result = JSON.parse(await rp.delete(rolesUrl, {
      body: JSON.stringify(deleteRoleRequest)
    }));
    return { success: true, data: result };
  } catch (e) {
    if (JSON.stringify(e.error).includes("ROLE_EXPIRED")) {
      authorize();
    }
    console.log("DeleteRole Failed: ", e.error);
    return { success: false, error: JSON.parse(e.error) };
  }
}

const styles = (theme: Theme) => createStyles({
  tableGrid: {
    marginTop: "10px"
  }
});

interface Props extends WithStyles<typeof styles> {
}

interface State {
  userRoleMap: { [key: string]: UserDetails },
  allUserMap: { [key: string]: AuthUserDetails },
  serviceRoleMap: { [key: string]: ServiceDetails }
}

class App extends React.Component<Props, State> {
  public constructor(props: Props) {
    super(props);
    this.state = { userRoleMap: {}, allUserMap: {}, serviceRoleMap: {} };
  }

  public async componentDidMount() {
    await getUserWithRolesForTenantService({ serviceId: 'role' });
    const allUserMap = await getAuthUserMap();
    const serviceRoleMap = await getAllServices();
    const userRoleMap = await getUserRoleMap(allUserMap, serviceRoleMap);
    this.setState({ userRoleMap, allUserMap, serviceRoleMap });
  }

  public render() {
    const { classes } = this.props;
    return (
      <MuiThemeProvider theme={muiTheme}>
        <link rel="stylesheet" href="https://fonts.googleapis.com/css?family=Roboto:300,400,500" />
        <link rel="stylesheet" href="https://fonts.googleapis.com/icon?family=Material+Icons" />
        <Grid container={true} justify="center">
          <Grid item={true} xs={12}>
            <NavBar />
          </Grid>
          <Grid item={true} xs={8} className={classes.tableGrid}>
            <RoleTable userRoleMap={this.state.userRoleMap} serviceRoleMap={this.state.serviceRoleMap} allUserMap={this.state.allUserMap} updateRolesHandler={this.updateRoles} />
          </Grid>
        </Grid>
      </MuiThemeProvider>
    );
  }

  private updateRoles = async (addRequests: AddRoleRequest[], deleteRequests: DeleteRoleRequest[]) => {
    const rolesUrl = getRoleLocation() + "/dev/v1/user/role";
    try {
      await Promise.all(deleteRequests.map(deleteRequest => deleteRole(deleteRequest, rolesUrl)));
      await Promise.all(addRequests.map(addRequest => addRole(addRequest, rolesUrl)));
      const updatedUserRoleMap = await getUserRoleMap(this.state.allUserMap, this.state.serviceRoleMap);
      await this.setState({
        ...this.state,
        userRoleMap: updatedUserRoleMap
      })
      return true;
    } catch (error) {
      console.log(error);
      return false;
    }
  }
}

export default withStyles(styles)(App);



// WEBPACK FOOTER //
// ./src/App.tsx